import { CheckCircle, Clock, PoundSterling } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

export const PricingSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50" id="pricing">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            TOR1 Application Service
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Professional assistance for your Transfer of Residence application
          </p>
        </div>

        {/* Problem Statement */}
        <div className="mb-12">
          <Card className="border-none shadow-xl bg-white">
            <CardContent className="pt-8 pb-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Why TOR1 Applications Can Be Complicated
                </h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Applying for a TOR1 order can be far more complicated than many people expect. The process involves detailed paperwork, strict requirements, and precise submission procedures. Even small mistakes or missing information can result in delays or the application being rejected, meaning you may have to start the process again.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Many applicants find the process time-consuming and confusing, especially when trying to ensure that all sections of the process are completed correctly and that the supporting information is properly prepared. Understanding what is required, how to present the information, and where to submit it can quickly become overwhelming.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Solution */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-3">
              That's where TOR1Services can help.
            </h3>
            <p className="text-xl text-gray-700">
              We specialise in preparing and submitting TOR1 applications quickly, accurately, and professionally.
            </p>
          </div>

          <Card className="border-none shadow-xl bg-gradient-to-br from-blue-900 to-blue-800 text-white">
            <CardContent className="pt-8 pb-8">
              <p className="text-xl text-blue-100 leading-relaxed mb-6 text-center">
                Our team understands the requirements and ensures your application is completed correctly the first time, helping you avoid unnecessary stress and delays.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Pricing Card */}
        <div className="mb-12">
          <Card className="border-4 border-blue-900 shadow-2xl bg-white max-w-3xl mx-auto">
            <CardContent className="pt-12 pb-12">
              <div className="text-center mb-8">
                <div className="inline-block bg-green-100 px-6 py-2 rounded-full mb-4">
                  <span className="text-green-800 font-bold text-lg">SIMPLE FIXED FEE</span>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-center justify-center mb-2">
                    <PoundSterling className="text-blue-900" size={48} />
                    <span className="text-7xl font-bold text-blue-900">95</span>
                  </div>
                  <p className="text-gray-600 text-lg">Complete TOR1 Application Service</p>
                </div>
              </div>

              <div className="bg-blue-50 p-8 rounded-lg mb-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  What's Included:
                </h4>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <CheckCircle className="text-green-500 mr-4 mt-1 flex-shrink-0" size={28} />
                    <div>
                      <p className="text-lg font-semibold text-gray-900">Prepare your TOR1 application</p>
                      <p className="text-gray-600">Complete form preparation with expert guidance</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="text-green-500 mr-4 mt-1 flex-shrink-0" size={28} />
                    <div>
                      <p className="text-lg font-semibold text-gray-900">Check all required details and documentation</p>
                      <p className="text-gray-600">Thorough review to ensure nothing is missing</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="text-green-500 mr-4 mt-1 flex-shrink-0" size={28} />
                    <div>
                      <p className="text-lg font-semibold text-gray-900">Ensure the form is completed correctly</p>
                      <p className="text-gray-600">Expert verification before submission</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="text-green-500 mr-4 mt-1 flex-shrink-0" size={28} />
                    <div>
                      <p className="text-lg font-semibold text-gray-900">Submit the application on your behalf</p>
                      <p className="text-gray-600">We handle the entire submission process to HMRC</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center mb-6">
                <p className="text-xl text-gray-700 mb-2">
                  Instead of spending hours navigating a complex process,<br />
                  <strong className="text-blue-900">let TOR1Services handle it for you.</strong>
                </p>
              </div>

              <div className="text-center mb-8">
                <div className="inline-block bg-blue-100 px-8 py-4 rounded-lg">
                  <p className="text-2xl font-bold text-blue-900 mb-1">Professional. Simple. Stress-free.</p>
                </div>
              </div>

              <div className="text-center">
                <Button 
                  size="lg" 
                  className="bg-blue-900 hover:bg-blue-800 text-lg px-8 py-4 shadow-xl hover:shadow-2xl transition-all"
                  onClick={scrollToContact}
                >
                  Get Your TOR1 Today
                </Button>
                <p className="text-sm text-gray-500 mt-4">Only £95 - Complete service included</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Benefits Footer */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <Card className="border-none shadow-lg text-center">
            <CardContent className="pt-6">
              <Clock className="mx-auto text-blue-900 mb-3" size={40} />
              <h4 className="font-bold text-gray-900 mb-2">Save Time</h4>
              <p className="text-gray-600 text-sm">Avoid hours of confusing paperwork</p>
            </CardContent>
          </Card>
          
          <Card className="border-none shadow-lg text-center">
            <CardContent className="pt-6">
              <CheckCircle className="mx-auto text-green-500 mb-3" size={40} />
              <h4 className="font-bold text-gray-900 mb-2">Done Right</h4>
              <p className="text-gray-600 text-sm">Completed correctly the first time</p>
            </CardContent>
          </Card>
          
          <Card className="border-none shadow-lg text-center">
            <CardContent className="pt-6">
              <PoundSterling className="mx-auto text-blue-900 mb-3" size={40} />
              <h4 className="font-bold text-gray-900 mb-2">Fixed Price</h4>
              <p className="text-gray-600 text-sm">Just £95 - no hidden costs</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
